/**
 * CLI feature-flag gate commands.
 *
 * Keeps org/user gate enable/disable handling separate from feature-flag
 * listing, lifecycle, rollout, product, and announcement commands.
 */

import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest, getTargetOrg } from './cli-helpers.js';

type FlagApiResponse = {
  ok: boolean;
  error?: string;
};

type OrgGateEnableConfig = {
  ok: true;
  provided: boolean;
  body: Record<string, unknown>;
  suffixParts: string[];
};

type OrgGateEnableConfigError = {
  ok: false;
  error: string;
};

export type OrgGateEnableConfigResult = OrgGateEnableConfig | OrgGateEnableConfigError;

const ENABLE_USAGE =
  'Usage: canary feature-flag enable <name> (--target-org <orgId> | --user <userId>)';
const DISABLE_USAGE =
  'Usage: canary feature-flag disable <name> (--target-org <orgId> | --user <userId>)';

export function hasOrgGateEnableConfig(argv: string[]): boolean {
  return (
    getArgValue(argv, '--rollout-override') !== undefined ||
    hasFlag(argv, '--exclude-from-analysis') ||
    hasFlag(argv, '--include-in-analysis')
  );
}

export function hasConflictingAnalysisFlags(argv: string[]): boolean {
  return hasFlag(argv, '--exclude-from-analysis') && hasFlag(argv, '--include-in-analysis');
}

function exitWithError(message: string, usage?: string): never {
  console.error(message);
  if (usage) console.error(usage);
  process.exit(1);
}

export function buildOrgGateEnableConfig(argv: string[]): OrgGateEnableConfigResult {
  // Per-org rollout config (org gates only). --rollout-override <0-100|clear>
  // sets a per-org override of the flag's global %; --exclude-from-analysis /
  // --include-in-analysis toggle whether this org's runs count in the A-B stats.
  const overrideRaw = getArgValue(argv, '--rollout-override');
  const excludeFromAnalysis = hasFlag(argv, '--exclude-from-analysis');
  const includeInAnalysis = hasFlag(argv, '--include-in-analysis');

  if (hasConflictingAnalysisFlags(argv)) {
    return {
      ok: false,
      error: 'Error: Provide either --exclude-from-analysis or --include-in-analysis, not both.',
    };
  }

  const body: Record<string, unknown> = {};
  const suffixParts: string[] = [];

  if (overrideRaw !== undefined) {
    if (overrideRaw === 'clear') {
      body.rolloutPercentageOverride = null;
      suffixParts.push('rollout override cleared');
    } else {
      const parsed = Number.parseInt(overrideRaw, 10);
      if (!Number.isInteger(parsed) || parsed < 0 || parsed > 100) {
        return {
          ok: false,
          error: "Error: --rollout-override must be an integer 0-100 or 'clear'.",
        };
      }
      body.rolloutPercentageOverride = parsed;
      suffixParts.push(`rollout override ${parsed}%`);
    }
  }

  if (excludeFromAnalysis) {
    body.excludeFromAnalysis = true;
    suffixParts.push('excluded from analysis');
  } else if (includeInAnalysis) {
    body.excludeFromAnalysis = false;
    suffixParts.push('included in analysis');
  }

  return {
    ok: true,
    provided: hasOrgGateEnableConfig(argv),
    body,
    suffixParts,
  };
}

export function formatEnableOrgGateSuccess(
  name: string,
  orgId: string,
  suffixParts: string[]
): string {
  return `Enabled ${name} for org ${orgId}${suffixParts.length ? ` (${suffixParts.join(', ')})` : ''}`;
}

export function formatEnableUserGateSuccess(name: string, userId: string): string {
  return `Enabled ${name} for user ${userId}`;
}

export function formatDisableOrgGateSuccess(name: string, orgId: string): string {
  return `Disabled ${name} for org ${orgId}`;
}

export function formatDisableUserGateSuccess(name: string, userId: string): string {
  return `Disabled ${name} for user ${userId}`;
}

export async function runEnableGateCommand(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const name = argv[0];
  const orgId = getTargetOrg(argv);
  const userId = getArgValue(argv, '--user');

  if (!name || name.startsWith('--')) {
    exitWithError('Error: Missing flag name.', ENABLE_USAGE);
  }

  if (orgId && userId) {
    exitWithError('Error: Provide either --target-org or --user, not both.');
  }

  if (!orgId && !userId) {
    exitWithError('Error: Provide either --target-org <orgId> or --user <userId>.', ENABLE_USAGE);
  }

  if (hasConflictingAnalysisFlags(argv)) {
    exitWithError(
      'Error: Provide either --exclude-from-analysis or --include-in-analysis, not both.'
    );
  }
  if (userId && hasOrgGateEnableConfig(argv)) {
    exitWithError(
      'Error: --rollout-override / --exclude-from-analysis apply to org gates only (use --target-org).'
    );
  }

  const orgGateConfig = buildOrgGateEnableConfig(argv);
  if (!orgGateConfig.ok) {
    exitWithError(orgGateConfig.error);
  }

  if (orgId) {
    const result = await apiRequest<FlagApiResponse>(
      apiUrl,
      token,
      'POST',
      `/superadmin/feature-flags/${encodeURIComponent(name)}/organizations/${encodeURIComponent(orgId)}`,
      orgGateConfig.body
    );

    if (!result.ok) {
      exitWithError(`Error: ${result.error}`);
    }

    console.log(formatEnableOrgGateSuccess(name, orgId, orgGateConfig.suffixParts));
  } else {
    const result = await apiRequest<FlagApiResponse>(
      apiUrl,
      token,
      'POST',
      `/superadmin/feature-flags/${encodeURIComponent(name)}/users/${encodeURIComponent(userId!)}`
    );

    if (!result.ok) {
      exitWithError(`Error: ${result.error}`);
    }

    console.log(formatEnableUserGateSuccess(name, userId!));
  }
}

export async function runDisableGateCommand(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const name = argv[0];
  const orgId = getTargetOrg(argv);
  const userId = getArgValue(argv, '--user');

  if (!name || name.startsWith('--')) {
    exitWithError('Error: Missing flag name.', DISABLE_USAGE);
  }

  if (orgId && userId) {
    exitWithError('Error: Provide either --target-org or --user, not both.');
  }

  if (!orgId && !userId) {
    exitWithError('Error: Provide either --target-org <orgId> or --user <userId>.', DISABLE_USAGE);
  }

  if (orgId) {
    const result = await apiRequest<FlagApiResponse>(
      apiUrl,
      token,
      'DELETE',
      `/superadmin/feature-flags/${encodeURIComponent(name)}/organizations/${encodeURIComponent(orgId)}`
    );

    if (!result.ok) {
      exitWithError(`Error: ${result.error}`);
    }

    console.log(formatDisableOrgGateSuccess(name, orgId));
  } else {
    const result = await apiRequest<FlagApiResponse>(
      apiUrl,
      token,
      'DELETE',
      `/superadmin/feature-flags/${encodeURIComponent(name)}/users/${encodeURIComponent(userId!)}`
    );

    if (!result.ok) {
      exitWithError(`Error: ${result.error}`);
    }

    console.log(formatDisableUserGateSuccess(name, userId!));
  }
}
